import React, { useEffect, useState } from 'react'
import constant from '../../../constant'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import { v3_inspection_Status_Update, v3_inspections } from '../../../actions/v3/inspection';
import moment from 'moment';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import { v4_fetch_mailbox_recipients, v4_mailbox_configuration } from '../../../actions/v4/mailBox';
import SendReport from './sendReport';
import { filterRecipientOption, mapRecipientOptions } from '../../MailBox/recipientSelectUtils';
import ExportColumnModal from '../../common/ExportColumnModal';
import { appendDateRangeToFileName, exportRowsToExcel } from '../../../utils';
import RemarksSelect from '../../common/RemarksSelect';

const getInspectionCommentsForExport = (item) => (
    item.comments?.map(c => `${c.COMMENTED_BY_NAME}: ${c.COMMENT}`).join('\n') || ''
);

const getInspectionAttendanceForExport = (item) => (
    item.attendance?.map(att => {
        const docs = att.documents?.map(doc => doc.ORIGINAL_FILE_NAME || doc.FILE_NAME).join(', ') || '';
        return `Date: ${att.INSPECTION_DATE}, Result: ${att.RESULT}, By: ${att.ATTENDED_BY_NAME}, Remark: ${att.REMARK || ''}, Docs: ${docs}`;
    }).join('\n') || ''
);

const getInspectionCommentCount = (item = {}) => {
    const apiCount = item.TOTAL_COMMENTS ?? item.COMMENT_COUNT;
    if (apiCount !== undefined && apiCount !== null) {
        const parsedCount = Number(apiCount);
        if (Number.isFinite(parsedCount)) return parsedCount;
    }

    if (Array.isArray(item.attendance)) return item.attendance.length;
    if (Array.isArray(item.comments)) return item.comments.length;
    return 0;
};

const inspectionExportColumns = [
    { key: 'inspectionName', label: 'Inspection Name', value: (item) => item.INSPECTION_NAME },
    { key: 'inspectionNumber', label: 'Inspection Number', value: (item) => item.INSPECTION_NUMBER },
    { key: 'projectName', label: 'Project Name', value: (item) => item.PROJECT_NAME },
    { key: 'shipName', label: 'Ship Name', value: (item) => item.SHIP_NAME },
    { key: 'hullNumber', label: 'Hull Number', value: (item) => item.HULL_NUMBER },
    { key: 'location', label: 'Location', value: (item) => item.LOCATION_NAME },
    { key: 'categoryName', label: 'Category Name', value: (item) => item.CATEGORY_NAME },
    { key: 'status', label: 'Status', value: (item) => item.STATUS },
    { key: 'statusComment', label: 'Status Comment', value: (item) => item.STATUS_COMMENT || '' },
    { key: 'scheduledDate', label: 'Scheduled Date', value: (item) => item.SCHEDULE_DATE || '' },
    { key: 'passedDate', label: 'Passed Date', value: (item) => item.PASSED_DATE || '' },
    { key: 'acceptedDate', label: 'Accepted Date', value: (item) => item.ACCEPTED_DATE || '' },
    { key: 'createdBy', label: 'Created By', value: (item) => item.CREATED_BY_NAME },
    { key: 'scheduledBy', label: 'Scheduled By', value: (item) => item.SCHEDULED_BY_NAME || '' },
    { key: 'acceptedBy', label: 'Accepted By', value: (item) => item.ACCEPTED_BY_NAME || '' },
    { key: 'passedBy', label: 'Passed By', value: (item) => item.PASSED_BY_NAME || '' },
    { key: 'createdOn', label: 'Created On', value: (item) => item.CREATED_ON },
    { key: 'modifiedOn', label: 'Modified On', value: (item) => item.MODIFIED_ON },
    { key: 'commentCount', label: 'Comment Count', value: getInspectionCommentCount },
    { key: 'comments', label: 'Comments', value: getInspectionCommentsForExport },
    { key: 'attendanceDetails', label: 'Attendance Details', value: getInspectionAttendanceForExport },
];

const Inspection = ({ projectId, shipId, setInspection, filterPayload, refresh, setRefresh, setStatusWiseCount, projectName, selectedHullLabel }) => {

    const [showFull, setShowFull] = useState(null); // store ID instead of boolean
    const [previewItem, setPreviewItem] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);


    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [inspectionId, setInspectionId] = useState('');
    const [mailBoxes, setMailBox] = useState([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [statusState, setStatusState] = useState({
        status: {
            name: 'status', value: '', error: '', options: ['Unscheduled', 'Scheduled', 'In Progress', 'Accepted', 'Rejected', 'Cancelled', 'Re-Inspection'], isRequired: true
        },
        remarks: { name: 'remarks', value: '', error: '', isRequired: false },
        sentEmail: { name: 'sentEmail', value: false, isRequired: false, error: '' },

        fromEmailId: { name: 'fromEmailId', value: '', error: '', isRequired: false },
        email: { name: 'email', value: [], options: [], error: '', isRequired: false },
        subject: { name: 'subject', value: '', error: '', isRequired: false },
        body: { name: 'body', value: '', error: '', isRequired: false },
        cc: { name: 'cc', value: [], options: [], error: '', isRequired: false },
    })

    const encodeFilterPayload = (payload) => {
        try {
            const json = JSON.stringify(payload);
            if (!json) return null;

            if (typeof TextEncoder !== 'undefined') {
                const bytes = new TextEncoder().encode(json);
                let binary = '';
                bytes.forEach((b) => {
                    binary += String.fromCharCode(b);
                });
                return btoa(binary);
            }

            // Fallback for older browsers.
            return btoa(unescape(encodeURIComponent(json)));
        } catch (error) {
            console.error('Failed to encode filter payload:', error);
            return null;
        }
    };

    useEffect(() => {
        // Reset pagination when scope or filters change to avoid mixing results.
        setPage(1);
        setData([]);
        setTotal(0);
        setLoadingMore(false);
    }, [projectId, shipId, filterPayload]);

    useEffect(() => {
        if (!previewItem) return;

        setLoading(true);

        const url = `${process.env.REACT_APP_BASE_URL}/v3/inspection/preview/${previewItem.ID}`;

        setPdfUrl(url);
        setLoading(false);

    }, [previewItem]);

    // useEffect(() => {
    //     const fetchPdf = async () => {
    //         if (!previewItem) return;

    //         setLoading(true);
    //         setPdfUrl(null);

    //         try {
    //             const response = await fetch(
    //                 `${process.env.REACT_APP_BASE_URL}/v3/inspection/download/pdf/${previewItem?.ID}`

    //             );

    //             if (!response.ok) throw new Error("Failed to download PDF");

    //             const blob = await response.blob();
    //             const fileUrl = URL.createObjectURL(blob);
    //             setPdfUrl(fileUrl);
    //         } catch (err) {
    //             console.error("PDF Preview Error:", err);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchPdf();

    //     // cleanup on close
    //     return () => {
    //         if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    //         setPdfUrl(null);
    //     };
    // }, [previewItem]);


    useEffect(() => {
        if (statusState.sentEmail.value && mailBoxes.length === 0) {
            v4_mailbox_configuration().then((res) => {
                if (res.status) {
                    setMailBox(res.result);

                }
            });
        }
    }, [statusState.sentEmail.value]);

    useEffect(() => {
        v4_fetch_mailbox_recipients().then((res) => {
            if (res.status) {
                const options = mapRecipientOptions(res.result);
                setStatusState((prev) => ({
                    ...prev,
                    ['email']: {
                        ...prev['email'], options
                    },
                    ['cc']: {
                        ...prev['cc'], options
                    },
                }));
            }
        })
    }, [])
    const handleStatusUpdateSubmit = () => {

        // 1. Validate required fields
        let isValid = true;
        const updatedState = { ...statusState };

        Object.keys(statusState).forEach((key) => {
            if (statusState[key].isRequired && !statusState[key].value) {
                updatedState[key].error = 'This field is required';
                isValid = false;
            }
        });

        setStatusState(updatedState);
        if (!isValid) {
            toast.error('Please fill all required fields.');
            return;
        }

        const toEmails = statusState.email.value.map((e) => e.value);
        const ccEmails = statusState.cc.value.map((e) => e.value);


        // 2. Build payload
        const payload = {
            remarks: statusState.remarks.value,
            remark: statusState.remarks.value,
            status: statusState.status.value,
            inspectionId: inspectionId,
            sentEmail: statusState.sentEmail.value,
            to: toEmails,
            subject: statusState.subject.value,
            html: statusState.body.value,
            id: statusState.fromEmailId.value,
            cc: ccEmails
        };

        v3_inspection_Status_Update(payload).then((res) => {
            if (res.status) {
                toast.success(res.result.message)
                setRefresh(!refresh)
                const modal = window.bootstrap.Modal.getInstance(document.getElementById(`statusUpdateModal`));
                if (modal) modal.hide();
            } else {
                toast.error(res.error?.message || res.error || 'Failed to update inspection status.');
            }
        })
    };
    useEffect(() => {
        const fetchInspections = async () => {
            try {
                const res = await v3_inspections(projectId, shipId, page, limit, encodeFilterPayload(filterPayload));
                if (res.status) {
                    const newData = res.result?.inspections || [];

                    setData(prev => {
                        // If it's the first page, replace data
                        if (page === 1) return newData;

                        // Otherwise, merge with previous data, avoiding duplicates
                        const existingIds = new Set(prev.map(item => item.ID));
                        const merged = [...prev];
                        newData.forEach(item => {
                            if (!existingIds.has(item.ID)) {
                                merged.push(item);
                            }
                        });
                        return merged;
                    });

                    setStatusWiseCount(res.result?.statusWiseCount);
                    setTotal(res.result?.total || 0);
                }
            } catch (err) {
                console.error("Failed to fetch inspections:", err);
            } finally {
                setLoadingMore(false);
            }
        };

        fetchInspections();
    }, [page, limit, projectId, shipId, filterPayload]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // If refresh is triggered → fetch all pages till current
                if (refresh) {
                    let allData = [];
                    for (let p = 1; p <= page; p++) {
                        const res = await v3_inspections(
                            projectId,
                            shipId,
                            p,
                            limit,
                            encodeFilterPayload(filterPayload)
                        );
                        if (res.status) {
                            allData = [...allData, ...(res.result?.inspections || [])];
                            if (p === page) {
                                setStatusWiseCount(res.result?.statusWiseCount);
                                setTotal(res.result?.total || 0);
                            }
                        }
                    }
                    setData(allData);
                    // ✅ If inspection detail exists, refresh it from the new list
                    setInspection((prevInspection) => {
                        if (!prevInspection) return prevInspection;
                        const updated = allData.find(item => item.ID === prevInspection.ID);
                        return updated || prevInspection; // fallback if not found
                    });
                } else {
                    // Normal fetch for the current page
                    const res = await v3_inspections(
                        projectId,
                        shipId,
                        page,
                        limit,
                        encodeFilterPayload(filterPayload)
                    );

                    if (res.status) {
                        const newData = res.result?.inspections || [];
                        setData(prev => {
                            if (page === 1) return newData;

                            const existingIds = new Set(prev.map(item => item.ID));
                            const merged = [...prev];
                            newData.forEach(item => {
                                if (!existingIds.has(item.ID)) {
                                    merged.push(item);
                                }
                            });
                            return merged;
                        });

                        setStatusWiseCount(res.result?.statusWiseCount);
                        setTotal(res.result?.total || 0);
                        // ✅ Update inspection if it exists
                        setInspection((prevInspection) => {
                            if (!prevInspection) return prevInspection;
                            const updated = newData.find(item => item.ID === prevInspection.ID);
                            return updated || prevInspection;
                        });
                    }
                }
            } catch (err) {
                console.error("Failed to fetch inspections:", err);
            }
        };

        fetchData();
    }, [refresh]);


    const fetchAllInspectionsForExport = async () => {
        const exportLimit = 200;
        let currentPage = 1;
        let allData = [];
        let totalCount = 0;

        while (true) {
            const res = await v3_inspections(
                projectId,
                shipId,
                currentPage,
                exportLimit,
                encodeFilterPayload(filterPayload)
            );

            if (!res?.status) {
                throw new Error(res?.error || 'Failed to fetch inspections for export.');
            }

            const pageData = res.result?.inspections || [];
            totalCount = Number(res.result?.total || totalCount);
            allData = [...allData, ...pageData];

            if (pageData.length === 0) break;
            if (totalCount && allData.length >= totalCount) break;

            currentPage += 1;
        }

        return allData;
    };

    const exportExcel = async (selectedColumns = inspectionExportColumns) => {
        try {
            setExporting(true);
            const expectedTotal = Number(total || 0);
            let exportData = data;

            if (!exportData || exportData.length === 0 || (expectedTotal && exportData.length < expectedTotal)) {
                exportData = await fetchAllInspectionsForExport();
            }

            if (!exportData || exportData.length === 0) {
                toast.warn('No data to export.');
                return;
            }

            exportRowsToExcel({
                rows: exportData,
                columns: selectedColumns,
                sheetName: "Inspections",
                fileName: `${appendDateRangeToFileName('inspections', filterPayload)}.xlsx`,
            });
        } catch (error) {
            console.error('Export failed:', error);
            toast.error(error?.message || 'Failed to export inspections.');
        } finally {
            setExporting(false);
        }
    };

    const handleStatusStateChange = (e) => {
        const { name, value } = e.target;
        setStatusState(prev => ({
            ...prev,
            [name]: { ...prev[name], value, error: '' }
        }));
    };
    const handleMultiSelectChange = (selectedOptions, name) => {
        setStatusState((prev) => ({
            ...prev,
            [name]: {
                ...prev[name],
                value: selectedOptions,
                error: '',
            },
        }));
    };

    const getShortDescription = (text = 'NA') => {
        if (!text) return "";
        if (text.length <= 50) return text;
        return text.substring(0, 50) + "...";
    };

    const handleLoadMore = () => {
        if (loadingMore || data.length >= total) return;
        setLoadingMore(true);
        setPage(prev => prev + 1);
    };
    return (
        <div className="inspection-right-section">
            <div className="inspection-title-box">
                <div className="inspection-one">
                    Inspection{selectedHullLabel ? ` - ${selectedHullLabel}` : ''}
                </div>
                <div className="inspection-two">


                    <button
                        type="button"
                        data-bs-toggle="modal"
                        data-bs-target="#inspection-export-columns-modal"
                        className="btn btn-sm btn-primary me-2"
                        disabled={exporting}
                        style={{
                            backgroundImage:
                                "linear-gradient(90deg, rgb(104, 149, 180), rgb(38, 85, 120))"
                        }}
                    >
                        <i className="mdi mdi-file-excel-outline label-icon" /> {exporting ? 'Exporting...' : 'Export'}
                    </button><Link
                        to={{
                            pathname: constant.Authedcomponent.inspectionCreate.url,
                            state: {
                                projectId,
                                projectName
                            }
                        }}
                        className="btn btn-sm btn-primary me-2"
                        style={{
                            backgroundImage:
                                "linear-gradient(90deg, rgb(104, 149, 180), rgb(38, 85, 120))"
                        }}
                    >
                        <i className='fa fa-plus-circle me-1' />
                        Create Inspection

                    </Link></div>
            </div>


            <div className="hull-right-section">
                {data?.map(item => {

                    const isExpanded = showFull === item.ID;
                    const commentCount = getInspectionCommentCount(item);
                    return (
                        <div className="fat-hull-item" key={item.ID} onClick={() => { setInspection(item) }}>
                            <div className="hull-top-box">
                                <div className="hull-top-left">
                                    <div className="align-items-left">
                                        <h5>{item.HULL_NUMBER}  - {item.SHIP_NAME}</h5>
                                        <h6>{item.INSPECTION_NAME} - {item.INSPECTION_NUMBER}</h6>

                                        <p
                                            style={{
                                                wordWrap: "break-word",     // wrap long words
                                                overflowWrap: "break-word",
                                                whiteSpace: "pre-wrap"      // preserve newlines and wrap text
                                            }}
                                        >
                                            <strong>Remark :</strong>{" "}
                                            {isExpanded
                                                ? item.DESCRIPTION?.length > 0
                                                    ? item.DESCRIPTION
                                                    : "NA"
                                                : item.DESCRIPTION?.length > 0
                                                    ? getShortDescription(item.DESCRIPTION)
                                                    : "NA"}

                                            {item.DESCRIPTION?.length > 50 && (
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // prevent parent click
                                                        setShowFull(isExpanded ? null : item.ID);
                                                    }}
                                                    style={{ color: "blue", cursor: "pointer", marginLeft: "5px" }}
                                                >
                                                    {isExpanded ? "View less" : "View more"}
                                                </span>
                                            )}
                                        </p>



                                        <div
                                            className="modal fade"
                                            id={`statusUpdateModal`}
                                            data-bs-backdrop="static"
                                            data-bs-keyboard="false"
                                            tabIndex={-1}
                                            role="dialog"
                                            aria-labelledby="statusUpdateModalLabel"
                                            aria-hidden="true"
                                        >
                                            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                                                <div className="modal-content">
                                                    <div className="modal-header">
                                                        <h5 className="modal-title" id="statusUpdateModalLabel">
                                                            Update Status
                                                        </h5>
                                                        <button
                                                            type="button"
                                                            className="btn-close"
                                                            data-bs-dismiss="modal"
                                                            aria-label="Close"
                                                        />
                                                    </div>
                                                    <div className="modal-body">
                                                        <div className="row">
                                                            <div className="col-md-12 mb-3">
                                                                <label>Status <span >*</span></label>
                                                                <select
                                                                    name={'status'}
                                                                    value={statusState['status'].value}
                                                                    onChange={handleStatusStateChange}
                                                                    className={`form-control ${statusState['status'].error ? 'is-invalid' : ''}`}
                                                                >
                                                                    <option value=''>Select Status</option>
                                                                    {statusState.status.options.map((status) => {
                                                                        return (
                                                                            <option value={status}>{status}</option>

                                                                        )
                                                                    })}
                                                                </select>
                                                                {statusState['status'].error && <div className="invalid-feedback">{statusState['status'].error}</div>}
                                                            </div>

                                                            <RemarksSelect
                                                                label="Remarks"
                                                                name="remarks"
                                                                value={statusState.remarks.value}
                                                                onChange={handleStatusStateChange}
                                                                error={statusState.remarks.error}
                                                                required={statusState.remarks.isRequired}
                                                                placeholder="Enter remarks"
                                                            />
                                                            <div className="col-md-12 mb-3">
                                                                <label>Send Email ? </label>
                                                                &nbsp;
                                                                &nbsp;
                                                                <input
                                                                    type="checkbox"
                                                                    name={'sentEmail'}
                                                                    checked={statusState.sentEmail.value === true}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;

                                                                        setStatusState(prev => ({
                                                                            ...prev,
                                                                            sentEmail: {
                                                                                ...prev.sentEmail,
                                                                                value: checked,
                                                                            },
                                                                            fromEmailId: {
                                                                                ...prev.fromEmailId,
                                                                                isRequired: checked,
                                                                            },
                                                                            email: {
                                                                                ...prev.email,
                                                                                isRequired: checked,
                                                                            },
                                                                            subject: {
                                                                                ...prev.subject,
                                                                                isRequired: checked,
                                                                            },
                                                                            body: {
                                                                                ...prev.body,
                                                                                isRequired: checked,
                                                                            },
                                                                        }));
                                                                    }}
                                                                    className={`mt-1 form-check-input ${statusState['sentEmail'].error ? 'is-invalid' : ''}`}
                                                                />
                                                            </div>
                                                            {statusState.sentEmail.value &&
                                                                <div>
                                                                    <div className="mb-3">
                                                                        <select
                                                                            isMulti
                                                                            name="fromEmailId"
                                                                            value={statusState.fromEmailId.value}
                                                                            onChange={handleStatusStateChange}
                                                                            className="form-select"
                                                                        >
                                                                            <option value={''}> Send From </option>
                                                                            {mailBoxes?.map((mail) => {
                                                                                return (
                                                                                    <option value={mail.ID}>{mail.EMAIL} </option>
                                                                                )
                                                                            })}
                                                                        </select>
                                                                        {statusState.fromEmailId.error && <small className="text-danger">{statusState.fromEmailId.error}</small>}
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <CreatableSelect
                                                                            isMulti
                                                                            name="email"
                                                                            value={statusState.email.value}
                                                                            options={statusState.email.options} // Optional: preload some emails if needed
                                                                            filterOption={filterRecipientOption}
                                                                            onChange={(e) => handleMultiSelectChange(e, 'email')}
                                                                            className="basic-multi-select"
                                                                            classNamePrefix="select"
                                                                            placeholder="To"
                                                                            isClearable
                                                                        />
                                                                        {statusState.email.error && <small className="text-danger">{statusState.email.error}</small>}
                                                                    </div>
                                                                    {statusState.email.value.length > 0 && <div className="mb-3">
                                                                        <CreatableSelect
                                                                            isMulti
                                                                            name="cc"
                                                                            value={statusState.cc.value}
                                                                            options={statusState.cc.options} // Optional: preload some emails if needed
                                                                            filterOption={filterRecipientOption}
                                                                            onChange={(e) => handleMultiSelectChange(e, 'cc')}
                                                                            className="basic-multi-select"
                                                                            classNamePrefix="select"
                                                                            placeholder="CC"
                                                                            isClearable
                                                                        />
                                                                        {statusState.cc.error && <small className="text-danger">{statusState.cc.error}</small>}
                                                                    </div>}
                                                                    <div className="mb-3">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            name="subject"
                                                                            value={statusState.subject.value}
                                                                            onChange={handleStatusStateChange}
                                                                            placeholder="Subject"
                                                                        />
                                                                        {statusState.subject.error && <small className="text-danger">{statusState.subject.error}</small>}
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <textarea
                                                                            className="form-control"
                                                                            rows="5"
                                                                            name="body"
                                                                            value={statusState.body.value}
                                                                            onChange={handleStatusStateChange}
                                                                            placeholder="Write your message..."
                                                                        />
                                                                        {statusState.body.error && <small className="text-danger">{statusState.body.error}</small>}
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className="mt-3">
                                                            <button type="submit" className="btn btn-success me-2"
                                                                onClick={() => {
                                                                    handleStatusUpdateSubmit()

                                                                }}
                                                            >Send</button>
                                                            <button className="btn btn-danger text-white" onClick={() => {
                                                                const modal = window.bootstrap.Modal.getInstance(document.getElementById(`statusUpdateModal`));
                                                                if (modal) modal.hide();
                                                            }}>Cancel</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="hull-top-right text-end">
                                    <div className="inspection-card-meta">
                                        <span className="inspection-card-date">{moment(item.CREATED_ON).format('MMM DD, YYYY')}</span>
                                        <span className="inspection-card-comment-count" title="Inspection comments">
                                            <i className="bx bx-message" /> {commentCount}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="inspection-card-status-action"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setInspectionId(item.ID);
                                        }}
                                        data-bs-toggle="modal"
                                        data-bs-target={`#statusUpdateModal`}
                                    >
                                        <i className="bx bx-edit" />
                                        <span>Update Status</span>
                                    </button>
                                </div>

                                <div
                                    className="modal fade"
                                    id={`sendMail-${item.ID}`}
                                    data-bs-backdrop="static"
                                    data-bs-keyboard="false"
                                    tabIndex={-1}
                                    role="dialog"
                                    aria-labelledby={`sendMailLabel-${item.ID}`}
                                    aria-hidden="true"
                                >
                                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title" id={`sendMailLabel-${item.ID}`}>
                                                    Send Report Mail
                                                </h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    data-bs-dismiss="modal"
                                                    aria-label="Close"
                                                />
                                            </div>
                                            <div className="modal-body">
                                                <SendReport
                                                    data={item}
                                                    modalId={`sendMail-${item.ID}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="hull-bottom-info">
                                <h5 className={
                                    item.STATUS === 'Passed' ? 'passed-ins' :
                                        item.STATUS === 'Accepted' ? 'accepted-ins' :
                                            item.STATUS === 'Scheduled' ? 'scheduled-ins' :
                                                item.STATUS === 'In Progress' ? 'inprogress-ins' :
                                                    item.STATUS === 'Cancelled' ? 'cancelled-ins' :
                                                        'unscheduled-ins'
                                }>
                                    {item.STATUS}
                                </h5>

                                <p>
                                    <span>
                                        {item.PASSED_DATE
                                            ? 'Passed Date:'
                                            : item.ACCEPTED_DATE
                                                ? 'Accepted Date:'
                                                : item.SCHEDULE_DATE
                                                    ? 'Scheduled Date:'
                                                    : ''}
                                    </span>{' '}
                                    {item.PASSED_DATE
                                        ? moment(item.PASSED_DATE).format('MMM DD YYYY')
                                        : item.ACCEPTED_DATE
                                            ? moment(item.ACCEPTED_DATE).format('MMM DD YYYY')
                                            : item.SCHEDULE_DATE
                                                ? moment(item.SCHEDULE_DATE).format('MMM DD YYYY')
                                                : ''}
                                </p>
                                <p>{item.CATEGORY_NAME}</p>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary me-2 mb-2"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(90deg, rgb(104, 149, 180), rgb(38, 85, 120))"
                                    }}
                                    data-bs-toggle="modal"
                                    data-bs-target={`#sendMail-${item.ID}`}
                                >
                                    <i className="fas fa-paper-plane label-icon" /> Send Report

                                </button>
                                {/* <a
                                    href={`${process.env.REACT_APP_BASE_URL}/v3/inspection/download/:type/:inspectionId`.replace(':type', 'pdf').replace(':inspectionId', item?.ID)}
                                    className="btn btn-sm btn-primary me-2 mb-2"
                                    target='_blank'
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(90deg, rgb(104, 149, 180), rgb(38, 85, 120))"
                                    }}
                                >
                                    <i className="fas fa-file label-icon" /> Export as PDF

                                </a> */}
                                <div className="btn-group me-2 mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{
                                            backgroundImage:
                                                "linear-gradient(90deg, rgb(104, 149, 180), rgb(38, 85, 120))",
                                        }}
                                    >
                                        <i className="fas fa-file label-icon" /> Export
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <a
                                                href={`${process.env.REACT_APP_BASE_URL}/v3/inspection/download/pdf/${item?.ID}`}
                                                target="_blank"
                                                className="dropdown-item"
                                            >
                                                PDF
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                href={`${process.env.REACT_APP_BASE_URL}/v3/inspection/download/docx/${item?.ID}`}
                                                target="_blank"
                                                className="dropdown-item"
                                            >
                                                DOC
                                            </a>
                                        </li>
                                        <li>
                                            <li>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => setPreviewItem(item)}
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#previewModal"
                                                >
                                                    Preview
                                                </button>
                                            </li>
                                        </li>


                                    </ul>
                                </div>
                                {/* <div
                                    className="modal fade"
                                    aria-hidden="true"
                                    id="previewModal"
                                    data-bs-backdrop="static"
                                    data-bs-keyboard="false"
                                    tabIndex={-1}
                                    role="dialog"
                                    aria-labelledby="previewModalLabel"
                                >
                                    <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Preview</h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    data-bs-dismiss="modal"
                                                    aria-label="Close"
                                                    onClick={() => setPreviewItem(null)}
                                                />
                                            </div>

                                            <div className="modal-body" style={{ height: "80vh" }}>
                                                {previewItem && <iframe
                                                    src={`${process.env.REACT_APP_BASE_URL}/v3/inspection/preview/${previewItem?.ID}`}
                                                    title="PDF Preview"
                                                    width="100%"
                                                    height="100%"
                                                    style={{ border: "none" }}
                                                />}
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
                                <div
                                    className="modal fade"
                                    aria-hidden="true"
                                    id="previewModal"
                                    data-bs-backdrop="static"
                                    data-bs-keyboard="false"
                                    tabIndex={-1}
                                    role="dialog"
                                    aria-labelledby="previewModalLabel"
                                >
                                    <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Preview</h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    data-bs-dismiss="modal"
                                                    aria-label="Close"
                                                    onClick={() => setPreviewItem(null)}
                                                />
                                            </div>

                                            <div
                                                className="modal-body d-flex justify-content-center align-items-center"
                                                style={{ height: "80vh" }}
                                            >
                                                {loading ? (
                                                    <div style={{ fontSize: "18px", fontWeight: "500", color: "#555" }}>
                                                        Loading...
                                                    </div>
                                                ) : pdfUrl ? (
                                                    <iframe
                                                        src={pdfUrl}
                                                        title="PDF Preview"
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: "none" }}
                                                    />
                                                ) : (
                                                    <p className="text-muted">No preview available</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Link
                                    to={{
                                        pathname: constant.Authedcomponent.inspectionEdit.url,
                                        state: {
                                            inspectionData: item,
                                            projectId,
                                            projectName
                                        },
                                    }}
                                    className="btn btn-sm btn-primary me-2 mb-2"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(90deg, rgb(104, 149, 180), rgb(38, 85, 120))"
                                    }}
                                >
                                    <i className="bx bx-edit label-icon" /> Edit

                                </Link>
                            </div>
                        </div>
                    )
                })}

                {data.length < total && (
                    <div className="d-flex justify-content-end mt-3">
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                )}

                <ExportColumnModal
                    modalId="inspection-export-columns-modal"
                    title="Select Inspection Columns"
                    columns={inspectionExportColumns}
                    onExport={exportExcel}
                    isExporting={exporting}
                />

            </div>
        </div>
    )
}

export default Inspection
